/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { notification, Tooltip } from 'antd';
import { DownloadOutlined, LikeOutlined, DislikeOutlined } from '@ant-design/icons';
import {
  downloadAiInsightPdf,
  saveBlobAsFile,
  submitAiInsightFeedback,
  normalizeUsage,
  pickUsageFromMessage,
  readCachedUsage,
} from '../../services/aiInsightsApi';
import { buildAskEvansPdfReport } from '../../utils/askEvansPdfReport';
import SpacerLogAiUsageLine from './SpacerLogAiUsageLine';

/**
 * Answer footer — one row:
 * Was this helpful? (left) · INPUT/OUTPUT/COST when API returns usage · Download PDF (right)
 */
const SpacerLogAiAnswerActions = ({
  markdown,
  usage,
  feedbackTargetId,
  initialRating = null,
  feature,
  question,
  projectId,
  ragSessionId,
  title = 'Ask Evans Report',
  eyebrow = 'SPACER LOG - ASK EVANS REPORT',
  subtitle = 'Prepared for operations - CONFIDENTIAL',
  showPdf = true,
}) => {
  const [rating, setRating] = useState(() => {
    if (initialRating === 'good' || initialRating === 1) return 'good';
    if (initialRating === 'bad' || initialRating === -1) return 'bad';
    return null;
  });
  const [pdfLoading, setPdfLoading] = useState(false);
  const [feedbackBusy, setFeedbackBusy] = useState(false);

  const usageNorm =
    normalizeUsage(usage)
    || pickUsageFromMessage({ usage, feedbackTargetId })
    || readCachedUsage(feedbackTargetId);

  const isGood = rating === 'good' || rating === 1;
  const isBad = rating === 'bad' || rating === -1;
  const locked = rating != null;

  const handlePdf = async () => {
    if (!markdown || pdfLoading) return;
    setPdfLoading(true);
    const stamp = new Date().toISOString().slice(0, 10);
    const slug = String(feature || 'report').replace(/[^a-z0-9_-]+/gi, '-').toLowerCase();
    const fileName = `ask-evans-${slug}-${stamp}.pdf`;
    try {
      let blob;
      try {
        blob = await buildAskEvansPdfReport({
          markdown,
          title,
          eyebrow,
          subtitle,
          question,
        });
      } catch (clientErr) {
        // eslint-disable-next-line no-console
        console.warn('Client PDF failed, trying API', clientErr);
        blob = await downloadAiInsightPdf({
          markdown,
          title,
          feature,
          question,
        });
      }
      if (!blob || !(blob instanceof Blob) || blob.size === 0) {
        throw new Error('PDF was empty.');
      }
      saveBlobAsFile(blob, fileName);
    } catch (err) {
      notification.error({
        message: 'PDF download failed',
        description: String(err?.message || err),
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleFeedback = async (next) => {
    if (!feedbackTargetId || feedbackBusy || locked) return;
    setFeedbackBusy(true);
    setRating(next);
    try {
      await submitAiInsightFeedback({
        feedbackTargetId,
        rating: next,
        feature,
        question,
        markdown,
        projectId,
        ragSessionId,
      });
    } catch (err) {
      setRating(null);
      notification.error({
        message: 'Could not send feedback',
        description: String(err?.message || err),
      });
    } finally {
      setFeedbackBusy(false);
    }
  };

  if (!markdown && !feedbackTargetId && !usageNorm) return null;

  return (
    <div className="spacer-ask-evans__answer-actions">
      <div className="spacer-ask-evans__answer-row">
        <div className="spacer-ask-evans__answer-row-left">
          {feedbackTargetId ? (
            <div className="spacer-ask-evans__helpful">
              <span className="spacer-ask-evans__helpful-label">Was this helpful?</span>
              <div
                className={`spacer-ask-evans__thumb-group${
                  locked ? ' spacer-ask-evans__thumb-group--locked' : ''
                }`}
                role="group"
                aria-label="Feedback"
              >
                <Tooltip title={isGood ? 'Marked helpful' : 'Helpful'}>
                  <span
                    className={`spacer-ask-evans__thumb-slot spacer-ask-evans__thumb-slot--up${
                      isGood ? ' is-good' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="spacer-ask-evans__thumb"
                      disabled={feedbackBusy || locked}
                      onClick={() => handleFeedback('good')}
                      aria-label="Thumbs up"
                      aria-pressed={isGood}
                    >
                      <LikeOutlined />
                    </button>
                  </span>
                </Tooltip>
                <span className="spacer-ask-evans__thumb-divider" aria-hidden />
                <Tooltip title={isBad ? 'Marked not helpful' : 'Not helpful'}>
                  <span
                    className={`spacer-ask-evans__thumb-slot spacer-ask-evans__thumb-slot--down${
                      isBad ? ' is-bad' : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="spacer-ask-evans__thumb"
                      disabled={feedbackBusy || locked}
                      onClick={() => handleFeedback('bad')}
                      aria-label="Thumbs down"
                      aria-pressed={isBad}
                    >
                      <DislikeOutlined />
                    </button>
                  </span>
                </Tooltip>
              </div>
            </div>
          ) : null}

          {usageNorm ? <SpacerLogAiUsageLine usage={usageNorm} inline /> : null}
        </div>

        {showPdf && markdown ? (
          <button
            type="button"
            className="spacer-ask-evans__pdf-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePdf();
            }}
            disabled={pdfLoading}
          >
            <DownloadOutlined />
            <span>{pdfLoading ? 'Preparing PDF…' : 'Download PDF Report'}</span>
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default SpacerLogAiAnswerActions;
